import { defineStore } from 'pinia'
import { subscriptionService } from '@/services/subscriptionService'
import type { StripeCustomer } from '@/types/subscription'
import { handleAuthError, extractErrorMessage } from '@/utils/storeHelpers'

export const useCustomerStore = defineStore('customer', {
  state: () => ({
    customer: null as StripeCustomer | null,
    loading: false,
    error: null as string | null,
  }),

  getters: {
    hasCustomer: (state) => !!state.customer,
    
    customerId: (state) => state.customer?.stripe_customer_id || null,
    
    customerEmail: (state) => state.customer?.email || null,
  },

  actions: {
    async fetchCustomer() {
      this.loading = true
      this.error = null

      try {
        const response = await subscriptionService.getCustomers()
        // API returns array of customers, get the first one
        this.customer = Array.isArray(response.data) ? response.data[0] : response.data
      } catch (error: any) {
        if (handleAuthError(error)) {
          this.error = 'Current user is not authorized to access customer data'
          return
        }

        this.error = extractErrorMessage(error, 'Failed to fetch customer')
      } finally {
        this.loading = false
      }
    },

    async createCustomer() {
      this.loading = true
      this.error = null

      try {
        // First try to get existing customer
        await this.fetchCustomer()

        if (this.customer?.stripe_customer_id) {
          return this.customer
        }

        const userId = localStorage.getItem('user_id')
        const email = this.customer?.email || 'user@example.com'

        const response = await subscriptionService.createCustomer({
          user: userId,
          email
        })

        this.customer = response.data
        return response.data
      } catch (error: any) {
        if (handleAuthError(error)) {
          this.error = 'Current user is not authorized to create customer'
          throw new Error('Current user is not authorized to create customer')
        }

        this.error = extractErrorMessage(error, 'Failed to create customer')
        throw error
      } finally {
        this.loading = false
      }
    },
  }
})

